import { Alert, AlertColor, Avatar, Box, IconButton, Menu, MenuItem, Snackbar, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { GenericTable, IFooterData } from '../GenericTable/GenericTable';
import { GetOneOrganization, GetOrganizationGroup, GetOrganizationUserTrim, GetOrganizationUserTrimName, SetUserAdmin, SetUserAdminSystem } from '../../../../../services/OrganizationServices';
import { User } from '../../../../../model/OrganizationModel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoaderComp from '../../../../../components/Loader/LoaderComp';
import { DisableUserAndGroupModal } from '../DisableUserAndGroupModal/DisableUserAndGroupModal';
import { DeleteUser, GetUser, ResetPassword, ResetPasswordLogged } from '../../../../../services/UserServices';
import { IResetPasswordLogged, PasswordReset, UserDelete } from '../../../../../model/UserModel';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { Group } from '../../../../../model/GroupModel';
import { useProfile } from '../../../../../context/ProfileProvider/useProfile';
import KeydriveSnackbar from '../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar';
import { KeyDriveChip } from '../../../../../components/KeydriveComponents/Tags/KeyDriveChip';
import { PaywallModal } from '../../../../../components/PaywallModal/PaywallModal';
import { AdminPanelSettings, HighQuality, PersonAdd } from '@mui/icons-material';
import useExtractPlan from '../../../../../hooks/useExtractPlan';
import { KeyDriveDialog } from '../../../../../components/KeydriveComponents/Dialog/KeyDriveDialog';
import { PersonRemove } from '@mui/icons-material';

interface IUserTable {
    nameOrg: string;
    search: string;
    isGroupPage: boolean;
    usersIdList?: string[];
    removeFromGroup?: (userId:string) => void;
    hasPhoneColumn?: boolean;
}

interface IMenuOptions {
    action: string;
    icon: JSX.Element;
    onClick: () => void;
}

interface IUserRow { 
    user: User; 
    nameOrg: string, 
    userGroups: Group[];
    setIsReload: React.Dispatch<React.SetStateAction<boolean>>;
    handleSnackbarWithMessage: (message: string, color: AlertColor) => void;
    isGroupPage: boolean;
    removeFromGroup: (userId: string) => void;
    hasPhoneColumn?: boolean;
}

type UserGroupsMap = { [userId: string]: Group[] };

const UserRow: React.FC<IUserRow> = ({ user, nameOrg, userGroups, setIsReload, handleSnackbarWithMessage, isGroupPage, removeFromGroup, hasPhoneColumn }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isModalDisableOpen, setIsModalDisableOpen] = useState<boolean>(false);
    const [userAlreadyAdmin , setUserAlreadyAdmin] = useState<boolean>(false);
    const [userAdminLoading, setUserAdminLoading] = useState<boolean>(false);
    const [userAlreadyAdminSystem, setUserAlreadyAdminSystem] = useState<boolean>(false);
    const profile = useProfile();
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const {t} = useTranslation();


    useEffect(() => {
        if (nameOrg == undefined) return;
        fetchUserRole();
        fetchUserCargo();
    }, [nameOrg]);

    function fetchUserRole() {
        setUserAdminLoading(true);
        GetOneOrganization(nameOrg)
        .then(data => {
            if (data?.adminUsers != undefined && data?.adminUsers?.includes(user.id!)) {
                setUserAlreadyAdmin(true);
                handleRoleText();
            } else {
                setUserAlreadyAdmin(false);
                handleRoleText();
            }
        })
        .catch(err => {
            handleSnackbarWithMessage(err.response?.data || err.message || 'Erro ao verificar cargo do usuário', 'error')
        })
        .finally(() => {
            setUserAdminLoading(false);
        });
    }

    function fetchUserCargo() {
        setUserAdminLoading(true);
        GetUser(user.id!)
        .then(data => {
            if (data?.adminSystem) {
                setUserAlreadyAdminSystem(true);
                handleCargoText();
            } else {
                setUserAlreadyAdminSystem(false);
                handleCargoText();
            }
        })
        .catch(err => {
            handleSnackbarWithMessage(err.response?.data || err.message || 'Erro ao verificar cargo do usuário', 'error')
        })
        .finally(() => {
            setUserAdminLoading(false);
        });
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDialog = () => {
        setIsModalDisableOpen(!isModalDisableOpen);
    };

    const handleConfirmDisable = () => {
        const userDelete: UserDelete = {
            id: user.id,
            organizationName: nameOrg,
        }

        DeleteUser(userDelete)
        .then((data) => {
            setIsModalDisableOpen(false);
            handleSnackbarWithMessage(t("UserExcluido.message"), 'success')
            setIsReload(true);
        })
        .catch((err) => handleSnackbarWithMessage(err.response?.data || err.message || 'Erro ao excluir usuário', 'error'))
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const resetPassword = (email: string) => {
        const passwordReset: IResetPasswordLogged = {
            login: email,
            userLoggedEmail: profile.login!
        }

        ResetPasswordLogged(passwordReset)
        .then((data) => handleSnackbarWithMessage(t("AdminConsole.ResetPassword"), 'success'))
        .catch((err) => handleSnackbarWithMessage(err.response?.data || err.message || 'Erro ao resetar senha', 'error'));
    }

    const optionsNomal: IMenuOptions[] = [
        ...(isGroupPage
            ? [{
                action: t("AdminConsole.RemoveGroup"),
                icon: <PersonRemoveIcon />,
                onClick:() => removeFromGroup(user.id),
            }]
            : []),
        {
            action: t("AdminConsole.DisableUser"),
            icon: <PersonOffIcon />,
            onClick: handleDialog,
        },
        {
            action: t("AdminConsole.EditUserInfo"),
            icon: <EditIcon />,
            onClick: () => navigate(`/${nameOrg}/adminConsole/usersAndGroups/newUser?userId=${user.id}`),
        },
        {
            action: handleRoleText(),
            icon: <PersonAdd />,
            onClick: () => {
                SetUserAdminAsync();
            }
        },
        ...(!user.isGuest
            ? [{
            action: t("AdminConsole.ResetPassword"),
            icon: <RefreshIcon />,
            onClick: () => resetPassword(user.login),
        }]
            : []),
    ];

    const optionsAdminSystem: IMenuOptions[] = [
        ...(isGroupPage
            ? [{
                action: t("AdminConsole.RemoveGroup"),
                icon: <PersonRemoveIcon />,
                onClick:() => removeFromGroup(user.id),
            }]
            : []),
        {
            action: t("AdminConsole.DisableUser"),
            icon: <PersonOffIcon />,
            onClick: handleDialog,
        },
        {
            action: t("AdminConsole.EditUserInfo"),
            icon: <EditIcon />,
            onClick: () => navigate(`/${nameOrg}/adminConsole/usersAndGroups/newUser?userId=${user.id}`),
        },
        {
            action: handleRoleText(),
            icon: <PersonAdd />,
            onClick: () => {
                SetUserAdminAsync();
            }
        },
        {
            action: handleCargoText(),
            icon: <AdminPanelSettings />,
            onClick: () => {
                SetUserAdminSystemAsync();
            }
        },
        ...(!user.isGuest
            ? [{
            action: t("AdminConsole.ResetPassword"),
            icon: <RefreshIcon />,
            onClick: () => resetPassword(user.login),
        }]
            : []),
    ];

    function handleRoleText(): string {
        if (userAdminLoading) return t("AdminConsole.Loading");

        return userAlreadyAdmin ? t("AdminConsole.UnSetUserAdmin") : t("AdminConsole.SetUserAdmin");
    }

    function handleCargoText(): string {
        if (userAdminLoading) return t("AdminConsole.Loading");

        return userAlreadyAdminSystem ? t("AdminConsole.UnSetUserAdminSystem") : t("AdminConsole.SetUserAdminSystem"); 
    }

    async function SetUserAdminSystemAsync() {
        try {
            await SetUserAdminSystem({
                nameOrg: nameOrg!,
                userId: user.id!
            });

            handleSnackbarWithMessage("O cargo do usuário foi atualizado com sucesso", 'success');
            fetchUserCargo();
        }  catch (err: any) {
            handleSnackbarWithMessage(err.response.data, 'error')
        }
    }

    async function SetUserAdminAsync() {
        try {
            await SetUserAdmin({
                nameOrg: nameOrg!,
                userId: user.id!
            });

            handleSnackbarWithMessage("O cargo do usuário foi atualizado com sucesso", 'success');
            fetchUserRole();
        }  catch (err: any) {
            handleSnackbarWithMessage(err.response.data, 'error')
        }
    }

    return (
        <>
            <TableRow>
                <TableCell sx={{ padding: '0px' }}>
                    <IconButton
                        aria-label="more"
                        id="long-button"
                        aria-controls={open ? 'long-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        id="long-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                    >
                        { !profile?.adminSystem ? optionsNomal.map((option, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => {
                                    option.onClick();
                                    handleClose();
                                }}
                                sx={{ gap: 1 }}
                            >
                                {option.icon}
                                {option.action}
                            </MenuItem>
                        )) : optionsAdminSystem.map((option, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => {
                                    option.onClick();
                                    handleClose();
                                }}
                                sx={{ gap: 1 }}
                            >
                                {option.icon}
                                {option.action}
                            </MenuItem>
                        ))}
                    </Menu>
                </TableCell>
                <TableCell sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    padding: '8px',
                 }}>
                    <Avatar sx={{ height: '26px', width: '26px' }} alt={user.name} />
                    <Tooltip title={user.name}>
                        <Typography component="span" sx={{
                            maxWidth: '300px',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                        }}>
                            {user.name}
                            { user.isGuest && (
                                <KeyDriveChip 
                                    text={t("Signture.Guest")}
                                />
                            ) }
                        </Typography>
                    </Tooltip>
                </TableCell>
                {isGroupPage ? 
                    <>
                    <TableCell sx={{ padding: '8px' }}>{user.login}</TableCell>
                        {hasPhoneColumn ? (
                            <TableCell sx={{ padding: '8px' }}>{user.phone ? user.phone : "(00) 00000-0000"}</TableCell>
                            ): 
                            <></>
                        }
                    </> 
                    : (
                    <>
                    <Tooltip title={user.login}>
                        <TableCell sx={{ 
                            padding: '8px',
                            maxWidth: '210px',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                        }}>{user.login}</TableCell>
                        </Tooltip>
                        <TableCell sx={{ padding: '8px' }}>{user.phone ? user.phone : "(00) 00000-0000"}</TableCell>
                        <TableCell sx={{ padding: '8px' }}>
                            <Typography sx={{ color: '#22638D',opacity: userGroups.length === 0 ? 0.5 : 1,}} >
                                {userGroups.length === 1 ? (
                                    <Typography component="span" onClick={() => navigate(`/${nameOrg}/adminConsole/usersAndGroups/${userGroups[0].id}`)} sx={{ "&:hover": { textDecoration: 'underline', cursor: 'pointer' } }}>
                                        {userGroups[0].name}
                                    </Typography>
                                ) : userGroups.length > 1 ? (
                                    <>
                                        <Typography component="span" onClick={() => navigate(`/${nameOrg}/adminConsole/usersAndGroups/${userGroups[0].id}`)} sx={{ "&:hover": { textDecoration: 'underline', cursor: 'pointer' }}}> 
                                            {userGroups[0].name}
                                        </Typography>
                                        <Typography sx={{marginLeft: "4px", "&:hover": { textDecoration: 'underline', cursor: 'pointer' } }} onClick={() => navigate(`/${nameOrg}/adminConsole/usersAndGroups/newUser?userId=${user.id}`)} component="span">
                                            + {userGroups.length - 1} 
                                        </Typography>
                                    </>
                                ) : (
                                        t("AdminConsole.DoesNotHave")
                                )}
                            </Typography>
                        </TableCell>
                    </>
                )}
            </TableRow>
            {/* <DisableUserAndGroupModal isopen={isModalDisableOpen} handleClickConfirm={handleConfirmDisable} handleOpenDialog={handleDialog} isUser /> */}
            <KeyDriveDialog 
                dialogTitleCenter
                 keyDriveDialogButtons={{
                    leftButtonMethod: () => { handleDialog() },
                    rightButtonMethod: () => {
                        handleConfirmDisable();
                        handleDialog();
                    },
                    rightButtonText: t("AdminConsole.Continue"),
                    leftButtonText: t("AdminConsole.Cancel")
                }}
                keyDriveDialogContent={{
                    dialogContent:t("AdminConsole.ConfirmIfYouReallyWantToDisableSelectedUser"),
                    dialogIcon: <PersonRemove sx={{ color: "#22638D", width: "44px", height: "44px" }}/>,
                    dialogTitle:t("AdminConsole.DisableModalTitle"),
                    handleTogglDialog: () => { handleDialog() },
                    openDialog: isModalDisableOpen
                }}
            />
        </>
    );
};

const UsersTable: React.FC<IUserTable> = ({ nameOrg, search, isGroupPage, usersIdList, removeFromGroup, hasPhoneColumn }) => {
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [totalUsers, setTotalUsers] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [userGroups, setUserGroups] = useState<UserGroupsMap>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [isReload, setIsReload] = useState<boolean>(false);
    const [messageSnack, setMessageSnack] = useState<string | undefined>(undefined);
    const [colorSnack, setColorSnack] = useState<AlertColor | undefined>();
    const [limit] = useState<number>(999);
    const USERS_PER_PAGE = 5;
    const { t } = useTranslation();

    const handleSnackbarWithMessage = (message: string, color: AlertColor) => {
        setMessageSnack(message)
        setColorSnack(color)
    }

    useEffect(() => {
        setLoading(true);
    
        GetOrganizationGroup(nameOrg, 0, limit)
            .then((data) => {
                const groupsMap: UserGroupsMap = {};
                data.forEach(group => {
                group.users?.forEach(user => {
                    if (groupsMap[user]) {
                        groupsMap[user].push(group);
                    } else {
                        groupsMap[user] = [group];
                    }
                });
                });

                setUserGroups(groupsMap);
            })
            .catch(err => handleSnackbarWithMessage(err.response?.data, 'error'))
            .finally(() => setLoading(false));

    }, [nameOrg]);

    useEffect(() => {
        if (!nameOrg) return;
        setLoading(true);

        GetOrganizationUserTrim(nameOrg, 0, limit)
            .then((data) => {
                if (isGroupPage) {
                    if (!usersIdList || usersIdList.length === 0) {
                        setAllUsers([]);
                        setTotalUsers(0);
                    } else {
                        const filtered = data.filter(user => usersIdList.includes(user.id!));
                        setAllUsers(filtered);
                        setTotalUsers(filtered.length);
                    }
                } else {
                    setAllUsers(data);
                    setTotalUsers(data.length);
                }
            })
            .finally(() => {
                setLoading(false);
                setIsReload(false);
            });
    }, [nameOrg, limit, isReload, isGroupPage, usersIdList]);

    const filteredUsers = useMemo(() => {
        if (!search || search.trim() === '') return allUsers;

        const lower = search.toLowerCase();
        return allUsers.filter(user =>
            user.name?.toLowerCase().includes(lower) ||
            user.login?.toLowerCase().includes(lower)
        );
    }, [allUsers, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);


    const displayedUsers = useMemo(
        () => filteredUsers.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE),
        [filteredUsers, currentPage]
    );

    const footerData: IFooterData = {
        dataType: t('AdminConsole.UsersMin'),
        showingQuantity: displayedUsers.length,
        totalQuantity: filteredUsers.length,
    };

    const headers = [
        t('AdminConsole.Name'),
        'E-mail',
        ...(hasPhoneColumn ? [t('AdminConsole.Phone')] : []),
        ...(isGroupPage? [] : [t('AdminConsole.Phone'), t('AdminConsole.Groups')]) 
    ];

    return (
        <>
            {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LoaderComp />
                </Box>
            ) : (
                <GenericTable
                    headers={headers}
                    bodyData={displayedUsers.map((user) => (
                        <UserRow removeFromGroup={removeFromGroup!} hasPhoneColumn={hasPhoneColumn} isGroupPage={isGroupPage} handleSnackbarWithMessage={handleSnackbarWithMessage!} setIsReload={setIsReload} key={user.id} user={user} nameOrg={nameOrg} userGroups={userGroups[user.id] || []} />
                    ))}
                    isGroupPage={isGroupPage}
                    footerData={footerData}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    dataPerPage={USERS_PER_PAGE}
                />
            )}

            <KeydriveSnackbar 
                open={messageSnack != undefined}
                message={messageSnack}
                variant={colorSnack}
                onClose={() => setMessageSnack(undefined)}
            />
        </>
    );
};

export default UsersTable;
